import * as React from "react";
import {
} from "@mantine/core";
import { AppLayout, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { PageActions, SavePlaygroundDataButton, ClearPlaygroundDataButton } from "@/components";
import { FileUploadDropzone, PlaygroundDataTable, PlaygroundSaveModal } from "@/components/playground";
import { useAuth } from "@bi-studio/shared/hooks";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getDataSources, savePlaygroundData } from "@bi-studio/shared/apis";
import { DataSource } from "@bi-studio/shared/lib";
import { notifications } from "@mantine/notifications";

export default function PlaygroundPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = React.useState<any[] | null>(null);
    const [columns, setColumns] = React.useState<string[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);

    // Save functionality state
    const [showSaveModal, setShowSaveModal] = React.useState(false);
    const [dataSources, setDataSources] = React.useState<DataSource[]>([]);
    const [selectedDataSource, setSelectedDataSource] = React.useState<string | null>(null);
    const [tableName, setTableName] = React.useState("");
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLoadingDataSources, setIsLoadingDataSources] = React.useState(true);



    React.useEffect(() => {
        if (token) {
            setIsLoadingDataSources(true);
            getDataSources(token)
                .then(setDataSources)
                .catch(console.error)
                .finally(() => setIsLoadingDataSources(false));
        }
    }, [token]);

    const handleBack = () => {
        navigate(-1);
    };

    const processFile = (file: File) => {
        setError(null);
        const reader = new FileReader();

        const extension = file.name.split('.').pop()?.toLowerCase();

        reader.onload = (e) => {
            try {
                const bstr = e.target?.result;
                if (extension === 'json') {
                    const json = JSON.parse(bstr as string);
                    if (Array.isArray(json)) {
                        setData(json);
                        if (json.length > 0) {
                            setColumns(Object.keys(json[0] as object));
                        }
                    } else if (typeof json === 'object' && json !== null) {
                        // Try to find an array property if the root is an object
                        const arrayProp = Object.values(json as object).find(val => Array.isArray(val));
                        if (arrayProp && Array.isArray(arrayProp)) {
                            setData(arrayProp);
                            if (arrayProp.length > 0) {
                                setColumns(Object.keys(arrayProp[0] as object));
                            }
                        } else {
                            throw new Error("JSON must be an array of objects or contain an array property.");
                        }
                    }
                } else {
                    const workbook = XLSX.read(bstr, { type: 'binary' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet);

                    if (Array.isArray(json) && json.length > 0) {
                        setData(json);
                        setColumns(Object.keys(json[0] as object));
                    } else {
                        throw new Error("File is empty or could not be parsed.");
                    }
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to parse file.");
                setData(null);
                setColumns([]);
            }
        };

        if (extension === 'json') {
            reader.readAsText(file);
        } else {
            reader.readAsBinaryString(file);
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            processFile(file);
        }
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = () => {
        setIsDragging(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const clearData = () => {
        setData(null);
        setColumns([]);
        setError(null);
    };


    const handleSave = async () => {
        if (!token || !selectedDataSource || !tableName || !data) return;

        try {
            setIsSaving(true);
            const result = await savePlaygroundData(token, selectedDataSource, tableName, data);
            notifications.show({
                title: 'Success',
                message: `Data saved to table ${result.tableName} (${result.rowCount} rows)`,
                color: 'green'
            });
            setShowSaveModal(false);
            setTableName("");
            setSelectedDataSource(null);
        } catch (err) {
            notifications.show({
                title: 'Error',
                message: err instanceof Error ? err.message : 'Failed to save data',
                color: 'red'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = () => {
        navigate("/login");
    };

    if (isLoadingDataSources) {
        return (
            <AppLayout onLogout={handleLogout} title="Playground" subtitle="Analytics Sandbox">
                <LoadingScreen message="Loading workspace..." />
            </AppLayout>
        );
    }

    return (
        <AppLayout onLogout={handleLogout} title="Playground" subtitle="Analytics Sandbox">
            <div className="space-y-4 md:space-y-6" style={{ position: "relative" }}>
                <PageActions
                    title="Playground"
                    onBack={handleBack}
                >
                    {data && (
                        <>
                            <SavePlaygroundDataButton onClick={() => setShowSaveModal(true)} />
                            <ClearPlaygroundDataButton onClick={clearData} />
                        </>
                    )}
                </PageActions>

                {!data ? (
                    <FileUploadDropzone
                        isDragging={isDragging}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onFileChange={handleFileChange}
                    />
                ) : (
                    <PlaygroundDataTable
                        columns={columns}
                        rows={data || []}
                    />
                )}

                {error && (
                    <ErrorState title="Import Error" message={error} showAction={false} />
                )}
            </div>

            <PlaygroundSaveModal
                opened={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                dataSources={dataSources}
                selectedDataSource={selectedDataSource}
                onSelectedDataSourceChange={setSelectedDataSource}
                tableName={tableName}
                onTableNameChange={setTableName}
                onSave={handleSave}
                isSaving={isSaving}
            />
        </AppLayout>
    );
}
